import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';

import { ownerResearchApi } from '../../requests/ownerResearch';
import { formatClock, formatNumber, formatRange, formatRunTime } from '../ListingSources/format';
import { Chevron, ModePill, StatusPill } from '../ListingSources/Pills';
import { CheckIcon, CountChips, CrossIcon, FailedStagePill, OUTCOMES, OUTCOME_FAILED, OUTCOME_LABEL, OutcomePill, WaitingStrip, noteOf } from './vocabulary';

// The line each answer leads with in the Why column.
const WHY_LABEL = {
    updated: 'Linked',
    created: 'Created',
    review: 'Stopped',
    skipped: 'Left',
    failed: 'Failed',
};

// How the parcel was settled, as the run wrote it, and how sure that reads.
const PARCEL_OK = ['Address + pin', 'Address', 'Pin'];

/** The run item as the review window takes it: the shape the review list serves. */
function reviewOf(item, run) {
    const place = noteOf(item, 'property');
    const [address, rest] = place ? place.text.split(' · ') : [item.external_id, ''];
    const review = noteOf(item, 'review');

    return {
        id: item.id,
        run_id: run.id,
        kind: review ? review.value : null,
        reason: item.reason,
        notes: item.notes || [],
        created_at: item.created_at,
        property: { id: item.property_id, address, city: rest || '', state: '', zip: '' },
    };
}

/** Open on whatever this run has most to say about. */
function firstOutcome(run) {
    if (run.review) return 'review';
    if (run.failed) return 'failed';
    if (run.updated) return 'updated';
    if (run.created) return 'created';
    return 'skipped';
}

function ParcelCheck({ item }) {
    const parcel = noteOf(item, 'parcel');

    if (!parcel) return <span className="or-none">—</span>;

    const ok = PARCEL_OK.indexOf(parcel.text) !== -1;

    return (
        <span className={'or-check ' + (ok ? 'or-check--ok' : 'or-check--bad')}>
            <span className="or-check__icon">{ok ? <CheckIcon size={10} /> : <CrossIcon />}</span>
            {parcel.text}
        </span>
    );
}

function Owner({ item }) {
    const owner = noteOf(item, 'owner');

    if (!owner || !owner.text) return <span className="or-none">—</span>;

    const [name, company] = owner.text.split(' · ');
    const saved = noteOf(item, 'saved');

    return (
        <div>
            <div className="or-owner__name">{name}</div>
            {(company || item.outcome === 'review') && (
                <div className="or-owner__company">
                    {[company, item.outcome === 'review' ? 'not saved' : null].filter(Boolean).join(' · ')}
                </div>
            )}
            {saved && saved.text !== name && (
                <div className="or-owner__company">
                    Saved as <Link to={'/investor/' + saved.value}>{saved.text}</Link>
                </div>
            )}
        </div>
    );
}

function Why({ item }) {
    const picked = noteOf(item, 'picked');
    const duplicates = noteOf(item, 'duplicates');
    const ai = noteOf(item, 'ai');

    return (
        <div>
            {picked && (
                <div className="or-why"><span className="or-why__label">Picked</span><span>{picked.text}</span></div>
            )}
            <div className="or-why"><span className="or-why__label">{WHY_LABEL[item.outcome]}</span><span>{item.reason}</span></div>
            {ai && (
                <div className="or-why or-why--ai"><span className="or-why__label">AI</span><span>{ai.text}</span></div>
            )}
            {duplicates && (
                <div className="ls-blank">
                    <span className="ls-blank__mark">?</span>
                    <span className="ls-blank__word">{duplicates.text}</span>
                </div>
            )}
        </div>
    );
}

/**
 * What one run did, property by property: who it picked, why, and how sure
 * it was of the parcel.
 */
export default function RunDetail({ run, onBack, onOpenReview, version }) {
    const [outcome, setOutcome] = useState(() => firstOutcome(run));
    const [items, setItems] = useState([]);
    const [meta, setMeta] = useState(null);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const unmounted = useRef(false);

    useEffect(() => () => {
        unmounted.current = true;
    }, []);

    const load = useCallback((wanted, filter) => {
        setLoading(true);
        setError(null);
        ownerResearchApi.runItems(run.id, filter, wanted).then(
            (envelope) => {
                if (unmounted.current) return;
                setItems(envelope.data || []);
                setMeta(envelope.meta ? envelope.meta.pagination : null);
                setPage(wanted);
                setLoading(false);
            },
            (failure) => {
                if (unmounted.current) return;
                setError(failure.message);
                setLoading(false);
            }
        );
    }, [run.id]);

    useEffect(() => {
        load(1, outcome);
    }, [load, outcome, version]);

    const lastPage = meta ? meta.last_page : 1;

    return (
        <div className="ls-detail">
            <button type="button" className="ls-backlink" onClick={onBack}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true">
                    <path d="M8.4 2.8 L4.4 6.5 L8.4 10.2" />
                </svg>
                All runs
            </button>

            <div className="ls-detail__head">
                <h1 className="ls-detail__title">{formatRunTime(run.started_at)}</h1>
                <StatusPill status={run.status} />
                <ModePill mode={run.mode} />
                <span className="ls-detail__time">
                    run #{run.id}
                    {run.finished_at ? ' · finished ' + formatClock(run.finished_at) : ''}
                    {run.started_by ? ' · ' + run.started_by : ''}
                    {' · ' + formatNumber(run.processed) + ' properties'}
                </span>
            </div>

            <CountChips run={run} />

            <WaitingStrip run={run} />
            {run.error && <div className="ls-detail__error">{run.error}</div>}

            <div className="ls-filters">
                {OUTCOMES.map((key) => (
                    <button
                        key={key}
                        type="button"
                        className={'ls-filter' + (outcome === key ? ' is-active' : '')}
                        onClick={() => setOutcome(key)}
                    >
                        {OUTCOME_LABEL[key]}
                        <span className={'ls-filter__count' + (run[key] ? '' : ' is-zero')}>{formatNumber(run[key])}</span>
                    </button>
                ))}
            </div>

            <div className="ls-card">
                {error && <div className="ls-empty">Could not load these properties. {error}</div>}
                {!error && loading && items.length === 0 && <div className="ls-empty">Loading properties…</div>}
                {!error && !loading && items.length === 0 && (
                    <div className="ls-empty">No {OUTCOME_LABEL[outcome].toLowerCase()} properties in this run</div>
                )}

                {items.length > 0 && (
                    <div>
                        <div className="ls-table__scroll">
                            <div className="ls-table ls-table--props">
                                <div className="ls-table__head">
                                    <div>Property</div>
                                    <div>Outcome</div>
                                    <div>Owner</div>
                                    <div>Why</div>
                                    <div>Parcel</div>
                                    <div />
                                </div>

                                {items.map((item) => {
                                    const place = noteOf(item, 'property');
                                    const [street, city] = place ? place.text.split(' · ') : [item.external_id, ''];

                                    const review = item.outcome === 'review' && onOpenReview ? () => onOpenReview(reviewOf(item, run)) : null;

                                    return (
                                        <div
                                            key={item.id}
                                            className={'ls-table__row' + (review ? '' : ' is-static')}
                                            role={review ? 'button' : undefined}
                                            tabIndex={review ? 0 : undefined}
                                            onClick={review || undefined}
                                            onKeyPress={review ? (event) => event.key === 'Enter' && review() : undefined}
                                        >
                                            <div>
                                                <div className="ls-table__primary">{street}</div>
                                                <div className="ls-table__sub">
                                                    {city ? city + ' · ' : ''}
                                                    {item.property_id
                                                        ? <Link className="ls-mono" to={'/property/' + item.property_id}>#{item.property_id}</Link>
                                                        : <span className="ls-mono">#{item.external_id}</span>}
                                                </div>
                                            </div>
                                            <div>
                                                <OutcomePill outcome={item.outcome} />
                                                {item.outcome === OUTCOME_FAILED && <div className="ls-table__sub"><FailedStagePill item={item} /></div>}
                                            </div>
                                            <div><Owner item={item} /></div>
                                            <Why item={item} />
                                            <div><ParcelCheck item={item} /></div>
                                            <div className="ls-table__chevron">{review && <Chevron />}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="ls-pager">
                            <span className="ls-pager__label">{formatRange(meta, items.length)}</span>
                            <button type="button" className="ls-btn ls-btn--small" disabled={page <= 1} onClick={() => load(page - 1, outcome)}>
                                Previous
                            </button>
                            <button type="button" className="ls-btn ls-btn--small" disabled={page >= lastPage} onClick={() => load(page + 1, outcome)}>
                                Next
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
